import { createContext, useContext } from 'react'

import { configs, type LiveHelperParams } from '@liveHelper/params'
import { useChatChannelQuery } from '@oneToOne/shared/hooks/useChatChannelQuery'
import type { ChatChannelConfig } from '@oneToOne/shared/services/rest/types'

import { resolveParams } from '@core/params'
import { useSetBusinessId } from '@core/tracking/funnelTracking/hooks/useSetBusinessId'

interface Props {
  widgetAttributes: Record<string, string>
  initialParams: LiveHelperParams
}

export const ChatChannelConfigContext = createContext<
  ChatChannelConfig | undefined
>(undefined)

/**
 * Hook to access the chat channel config context.
 * @deprecated Use `useContext(ChatChannelConfigContext)` instead.
 */
export function useChatChannelConfig(): ChatChannelConfig | undefined {
  return useContext(ChatChannelConfigContext)
}

/** Provides chat channel config context. */
export const ChatChannelConfigContextProvider: React.FC<
  React.PropsWithChildren<Props>
> = ({ widgetAttributes, initialParams, children }) => {
  // Resolve the chat channel id and api host from the current params source
  // first to execute the chat channel query.
  const { chat_channel_id, api_host } = resolveParams(
    {
      defaultParams: initialParams,
      widgetAttributes,
    },
    configs,
    /* silent */ true,
  )

  const { data } = useChatChannelQuery(chat_channel_id, api_host)

  useSetBusinessId(data?.business_id)

  if (!data) {
    // Block rendering until the chat channel config is available.
    return null
  }

  return (
    <ChatChannelConfigContext.Provider value={data}>
      {children}
    </ChatChannelConfigContext.Provider>
  )
}
